/**
 * Registration Request Validation Schemas
 *
 * Validation schemas for registration request submission and completion.
 */

import { z } from 'zod';
import {
  emailSchema,
  tenantCodeSchema,
  nonEmptyStringSchema,
  countrySchema,
  citySchema,
  addressRequiredSchema,
  phoneNumberRequiredSchema,
  nameSchema,
  passwordSchema,
} from './common';

// ============================================================================
// REGISTRATION REQUEST SUBMISSION VALIDATION
// ============================================================================

/**
 * Registration request submission schema (public endpoint)
 * Used when a prospect submits interest in using Ketrics
 */
export const createRegistrationRequestSchema = z
  .object({
    companyName: nonEmptyStringSchema
      .min(2, 'Company name must be at least 2 characters')
      .max(100, 'Company name must be at most 100 characters'),
    contactName: nonEmptyStringSchema
      .min(2, 'Contact name must be at least 2 characters')
      .max(100, 'Contact name must be at most 100 characters'),
    contactEmail: emailSchema,
    useCase: z
      .string()
      .max(500, 'Use case must be at most 500 characters')
      .trim()
      .optional(),
  })
  .strict();

export type CreateRegistrationRequestValidation = z.infer<typeof createRegistrationRequestSchema>;

// ============================================================================
// REGISTRATION COMPLETION VALIDATION
// ============================================================================

/**
 * Registration completion schema (from approved token)
 * Used when completing tenant registration after approval
 */
export const completeRegistrationSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    tenantName: nonEmptyStringSchema
      .min(1, 'Tenant name must be at least 1 character')
      .max(200, 'Tenant name must not exceed 200 characters'),
    firstName: nameSchema,
    lastName: nameSchema,
    password: passwordSchema,
    country: countrySchema,
    city: citySchema,
    address: addressRequiredSchema,
    phoneNumber: phoneNumberRequiredSchema,
  })
  .strict();

export type CompleteRegistrationValidation = z.infer<typeof completeRegistrationSchema>;
